import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { HeroSection } from "@/components/HeroSection";
import { HotDealsSection } from "@/components/HotDealsSection";
import { TrustSection } from "@/components/TrustSection";
import { FeaturedProductsSection } from "@/components/FeaturedProductsSection";
import { CarCoverVideoShowcase } from "@/components/CarCoverVideoShowcase";
import { ShoeDisplayShowcase } from "@/components/ShoeDisplayShowcase";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { ArrowLeft, ArrowRight, Phone, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isRTL, useLanguageStore } from "@/stores/languageStore";
import pageBgVideo from "@/assets/page-bg.mp4";

// Category images from Shopify products
const categoryBanners = [
  {
    id: "screwdrivers",
    translationKey: "categories.screwdrivers",
    descKey: "categories.screwdriversDesc",
    image: "https://cdn.shopify.com/s/files/1/0661/3611/3265/files/3.avif?v=1767280275",
  },
  {
    id: "sets",
    translationKey: "categories.sets",
    descKey: "categories.setsDesc",
    image: "https://cdn.shopify.com/s/files/1/0661/3611/3265/files/seen.avif?v=1767279765",
  },
];

const Index = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  const arrowSpacing = rtl ? "mr-2" : "ml-2";

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Header />

      <main className="flex-1 overflow-x-hidden">
        <HeroSection />

        {/* Top Contact Banner */}
        <section className="py-6 bg-charcoal relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-gold/10 to-primary/5" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-gold/20 rounded-full blur-3xl" />
          </div>
          <div className="container relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
            >
              <div className="flex items-center gap-4">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-primary via-primary to-gold/80 flex items-center justify-center shadow-xl ring-2 ring-primary/30 ring-offset-2 ring-offset-charcoal"
                >
                  <Heart className="h-6 w-6 text-white fill-white/30" />
                </motion.div>
                <div className="flex flex-col">
                  <span className="text-gold text-sm font-medium">{t("home.contactComfort")}</span>
                  <span className="text-white text-lg md:text-xl font-display font-semibold">{t("home.contactAvailable")}</span>
                </div>
              </div>
              <motion.a 
                href="tel:054-7677336"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-3 px-7 py-3 rounded-full gradient-gold text-white shadow-elegant font-bold text-lg group"
              >
                <Phone className="h-5 w-5 group-hover:animate-pulse" />
                <span className="direction-ltr">054-7677336</span>
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Hot Deals Section */}
        <HotDealsSection />

        {/* Car Cover Video Showcase */}
        <CarCoverVideoShowcase />

        {/* Shoe Display Box Showcase */}
        <ShoeDisplayShowcase />

        {/* Featured Products - Car Cover, Dash Cam, Scanner */}
        <FeaturedProductsSection />

        {/* Categories Section */}
        <section className="py-20 bg-muted/50">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold">{t("categories.title")}</h2>
              <p className="text-foreground/70 mt-2">{t("categories.description")}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {categoryBanners.map((category) => (
                <Link 
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group relative overflow-hidden rounded-2xl aspect-[4/3] md:aspect-[16/9]"
                >
                  {/* Background Image */}
                  <img 
                    src={category.image} 
                    alt={t(category.translationKey)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/40 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                      {t(category.translationKey)}
                    </h3>
                    <p className="text-white/70 text-sm md:text-base mb-4 max-w-md">
                      {t(category.descKey)}
                    </p>
                    <div className="flex items-center text-primary font-medium group-hover:gap-2 transition-all">
                      <span>{t("categories.viewProducts")}</span>
                      <ArrowIcon className={`h-4 w-4 ${arrowSpacing} transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1`} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* View All Categories Link */}
            <div className="text-center mt-8">
              <Link to="/categories">
                <Button variant="outline" size="lg">
                  {t("home.viewAllCategories")}
                  <ArrowIcon className={`${arrowSpacing} h-4 w-4`} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* Contact Comfort Section */}
        <section className="py-12 bg-charcoal relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/5 via-primary/10 to-gold/5" />
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/2 left-10 w-48 h-48 bg-primary/30 rounded-full blur-3xl" />
            <div className="absolute top-1/2 right-10 w-56 h-56 bg-gold/30 rounded-full blur-3xl" />
          </div>
          <div className="container relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12"
            >
              <div className="flex items-center gap-5">
                <motion.div 
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-primary via-primary to-gold/80 flex items-center justify-center shadow-2xl ring-4 ring-primary/20 ring-offset-4 ring-offset-charcoal"
                >
                  <Heart className="h-8 w-8 text-white fill-white/30" />
                </motion.div>
                <div className="flex flex-col">
                  <span className="text-gold text-base font-medium">{t("home.contactComfort")}</span>
                  <span className="text-white text-2xl md:text-3xl font-display font-bold">{t("home.contactAvailable")}</span>
                </div>
              </div>
              <motion.a 
                href="tel:054-7677336"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-4 px-10 py-4 rounded-full gradient-gold text-white shadow-elegant font-bold text-xl group"
              >
                <Phone className="h-6 w-6 group-hover:animate-pulse" />
                <span className="direction-ltr">054-7677336</span>
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Trust Section */}
        <TrustSection />

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden text-white">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={pageBgVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-charcoal/60" />
          <div className="container text-center relative z-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("home.ctaTitle")}</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">{t("home.ctaDescription")}</p>
            <Link to="/products">
              <Button size="lg" className="gradient-gold text-white shadow-elegant">
                {t("home.ctaButton")}
                <ArrowIcon className={`${arrowSpacing} h-5 w-5`} />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
